import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

/**
 * Animates an element's numeric text content:
 *  - counts up / down from the previous value to the new one when the value
 *    changes (e.g. tab count badges rolling to the new total), and
 *  - pulses continuously (a gentle scale "breathing") while
 *    `animatedNumberActive` is true — e.g. while a search filter is applied,
 *    so the counts keep indicating they're filtered, and stop once cleared.
 *
 * Usage:
 *   <span [appAnimatedNumber]="count" [animatedNumberActive]="!!searchText"></span>
 */
@Directive({
  selector: '[appAnimatedNumber]',
  standalone: false,
})
export class AnimatedNumberDirective implements OnChanges, OnDestroy {
  @Input('appAnimatedNumber') value: number | string = 0;
  /** While true, the badge pulses continuously (e.g. an active search). */
  @Input() animatedNumberActive = false;
  @Input() animatedNumberDuration = 600;

  private current = 0;
  private frameId: number | null = null;
  private pulseAnim: Animation | null = null;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {
    // A scale transform is ignored on plain inline elements, so make the badge
    // an inline-block (visually identical for a small pill) for the pulse.
    this.el.nativeElement.style.display = 'inline-block';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      const target = Number(this.value) || 0;
      if (target !== this.current) {
        this.countTo(this.current, target);
        this.current = target;
      } else {
        this.render(target);
      }
    }

    if (changes['animatedNumberActive']) {
      if (this.animatedNumberActive) {
        this.startPulse();
      } else {
        this.stopPulse();
      }
    }
  }

  ngOnDestroy(): void {
    this.cancel();
    this.stopPulse();
  }

  private countTo(from: number, to: number): void {
    this.cancel();
    const duration = this.animatedNumberDuration;
    // Run the rAF loop outside Angular so it doesn't trigger change detection
    // on every frame; we only mutate this element's text node directly.
    this.zone.runOutsideAngular(() => {
      const start = performance.now();
      const step = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
        this.render(Math.round(from + (to - from) * eased));
        if (progress < 1) {
          this.frameId = requestAnimationFrame(step);
        } else {
          this.frameId = null;
        }
      };
      this.frameId = requestAnimationFrame(step);
    });
  }

  private startPulse(): void {
    const el = this.el.nativeElement;
    if (typeof el.animate !== 'function' || this.pulseAnim) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.pulseAnim = el.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.25)' },
          { transform: 'scale(1)' },
        ],
        { duration: 900, easing: 'ease-in-out', iterations: Infinity },
      );
    });
  }

  private stopPulse(): void {
    this.pulseAnim?.cancel();
    this.pulseAnim = null;
  }

  private render(val: number): void {
    this.el.nativeElement.textContent = String(val);
  }

  private cancel(): void {
    if (this.frameId != null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }
}
